// src/pages/QuizPage/components/QuizResult.tsx
import { useNavigate } from "react-router-dom";
import { CheckCircle, AlertTriangle } from "lucide-react";

const MAX_WRONG = 3;

interface QuizResultProps {
  mode: string | undefined;
  questionsLength: number;
  correctCount: number;
  wrongCount: number;
  isMarafonMode: boolean;
  isMistakeMode: boolean;
  isMistakeEmpty: boolean; // mistake mode + bo'sh
  isTopicMode: boolean;
  isTicketMode: boolean;
  isNazoratMode: boolean;
  onRetry: () => void;
  t: (key: string) => string;
}

export default function QuizResult({
  mode,
  questionsLength,
  correctCount,
  wrongCount,
  isMarafonMode,
  isMistakeMode,
  isMistakeEmpty,
  isTopicMode,
  isTicketMode,
  isNazoratMode,
  onRetry,
  t,
}: QuizResultProps) {
  const navigate = useNavigate();

  // ── Mistakes empty UI ──
  if (isMistakeMode && isMistakeEmpty) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E1A] px-4">
        <div className="bg-[#0D1526] border border-white/10 p-8 rounded-[32px] max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h2 className="text-2xl font-black text-white mb-3">
            {t("noMistakes")}
          </h2>
          <p className="text-gray-400 text-sm mb-8">{t("noMistakesDesc")}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-500 transition">
            Bosh sahifaga qaytish
          </button>
        </div>
      </div>
    );
  }

  // ── Hisoblar ──
  const pct =
    questionsLength > 0 ?
      Math.round((correctCount / questionsLength) * 100)
    : 0;
  const failedByWrong = isNazoratMode && wrongCount >= MAX_WRONG;
  const passed = isMarafonMode ? true : !failedByWrong && pct >= 85;

  // ── Orqaga tugma manzili ──
  const { backButtonPath, backButtonLabel } = (() => {
    if (isTopicMode) {
      return {
        backButtonPath: "/topics",
        backButtonLabel: "Mavzularga qaytish",
      };
    }
    if (isTicketMode) {
      return {
        backButtonPath: "/tickets",
        backButtonLabel: "Biletlarga qaytish",
      };
    }
    if (
      isMarafonMode ||
      mode === "time-attack" ||
      mode === "duel" ||
      isNazoratMode ||
      isMistakeMode
    ) {
      return {
        backButtonPath: "/dashboard",
        backButtonLabel: "Bosh sahifaga qaytish",
      };
    }
    return {
      backButtonPath: "/dashboard",
      backButtonLabel: "Bosh menyu",
    };
  })();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0E1A] px-4">
      <div className="bg-[#0D1526] border border-white/10 p-8 rounded-[32px] max-w-md w-full text-center shadow-2xl">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            passed ? "bg-green-500/20" : "bg-red-500/20"
          }`}>
          {passed ?
            <CheckCircle size={40} className="text-green-400" />
          : <AlertTriangle size={40} className="text-red-400" />}
        </div>
        <h2 className="text-2xl font-black text-white mb-2">
          {isMarafonMode ?
            "Marafon yakunlandi 🏁"
          : passed ?
            "Tabriklaymiz! 🎉"
          : "Qayta urinib ko'ring"}
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          {failedByWrong ? `${MAX_WRONG} ta xato — imtihon to'xtatildi` : ""}
        </p>
        <div className="bg-white/5 border border-white/10 p-6 rounded-2xl mb-6">
          <p
            className="text-5xl font-black mb-2"
            style={{ color: passed ? "#4ADE80" : "#F87171" }}>
            {isMarafonMode ? correctCount : `${pct}%`}
          </p>
          <p className="text-gray-400 text-sm">
            {correctCount} to'g'ri / {wrongCount} xato
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(backButtonPath)}
            className="flex-1 py-4 bg-white/5 border border-white/10 text-white rounded-2xl font-bold hover:bg-white/10 transition">
            {backButtonLabel}
          </button>
          <button
            onClick={onRetry}
            className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-500 transition">
            Qayta
          </button>
        </div>
      </div>
    </div>
  );
}
